import { LocalDate } from "@js-joda/core";
import { CustomIcon } from "components/icon/customIcon.component";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { Modal } from "components/designV2/modal/modal";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { parseIsoDateToLocalDateOrNull } from "web-apps/company/utils/timeAndDate";
import type { SelectOption } from "../../profilePanel.contract";
import { canApplyMonthlyEffectiveDate } from "../profilePanel.effectiveDate.logic";
import {
  canDeleteHistoryItemForEmploymentStartDate,
  canDeleteHistoryItemWithinLastMonthOrFuture,
  sortHistoryItemsByStartDateDesc,
} from "../profilePanel.history.logic";
import {
  SalaryModalAddAction,
  SalaryModalDeleteButton,
  SalaryModalError,
  SalaryModalFormGrid,
  SalaryModalHistoryCell,
  SalaryModalHistoryHeaderCell,
  SalaryModalHistoryHeaderRow,
  SalaryModalHistoryRow,
  SalaryModalHistorySection,
  SalaryModalHistoryTable,
  SalaryModalHistoryTitle,
} from "../salary/profilePanel.salaryModal.styled";

interface TaxHistoryItem {
  id: number;
  startDate: string;
  value: number | null;
}

interface ProfilePanelTaxModalProps {
  isOpen: boolean;
  isSaving: boolean;
  saveError: string;
  taxTable: string;
  effectiveFromDate: LocalDate | null;
  earliestSelectableDate: LocalDate | null;
  employmentStartDate: string | null;
  taxOptions: SelectOption[];
  taxHistory: TaxHistoryItem[];
  onClose: () => void;
  onConfirm: () => void;
  onAddTax: () => void;
  onDeleteTax: (taxId: number) => void;
  onChangeFromDate: (date: LocalDate | null) => void;
  onTaxTableChange: (taxTable: string) => void;
}

export const ProfilePanelTaxModal = (
  props: ProfilePanelTaxModalProps,
): JSX.Element => {
  const { t } = useTranslation();

  const sortedHistory = useMemo(() => {
    return sortHistoryItemsByStartDateDesc(props.taxHistory);
  }, [props.taxHistory]);

  const canAddTax = canApplyMonthlyEffectiveDate(
    props.effectiveFromDate,
    props.earliestSelectableDate,
  );

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      label={t("Staff.ProfilePanelChangeTax")}
      size="compact"
      footer={
        <>
          <SecondaryButton
            label={t("General.Cancel")}
            disabled={props.isSaving}
            onClick={props.onClose}
          />
          <PrimaryButton
            label={t("General.Save")}
            isLoading={props.isSaving}
            disabled={props.isSaving}
            onClick={props.onConfirm}
          />
        </>
      }
    >
      <SalaryModalFormGrid>
        <Dropdown
          fullWidth
          label={t("Staff.ProfilePanelTaxNewTaxTable")}
          options={props.taxOptions}
          value={props.taxTable}
          onChange={props.onTaxTableChange}
        />
        <DatePicker
          label={t("Staff.ProfilePanelTaxStartDay")}
          value={props.effectiveFromDate}
          onChange={props.onChangeFromDate}
          monthlySelectableDay={1}
          earliestSelectableDate={props.earliestSelectableDate ?? undefined}
        />
        <SalaryModalAddAction>
          <PrimaryButton
            size="small"
            label={t("General.Add")}
            isLoading={props.isSaving}
            disabled={!canAddTax || props.isSaving}
            onClick={props.onAddTax}
          />
        </SalaryModalAddAction>
      </SalaryModalFormGrid>

      <SalaryModalHistorySection>
        <SalaryModalHistoryTitle>
          {t("General.History")}
        </SalaryModalHistoryTitle>
        <SalaryModalHistoryTable>
          <SalaryModalHistoryHeaderRow $columns={3}>
            <SalaryModalHistoryHeaderCell>
              {t("Staff.AddStaffPanelFieldStartDate")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell $isRightAligned>
              {t("Staff.AddStaffPanelFieldTaxTable")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell $isCentered>
              {"\u00A0"}
            </SalaryModalHistoryHeaderCell>
          </SalaryModalHistoryHeaderRow>
          {sortedHistory.length === 0 ? (
            <SalaryModalHistoryRow $columns={3}>
              <SalaryModalHistoryCell>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell $isRightAligned>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell $isCentered>-</SalaryModalHistoryCell>
            </SalaryModalHistoryRow>
          ) : (
            sortedHistory.map((taxItem) => (
              <SalaryModalHistoryRow key={taxItem.id} $columns={3}>
                <SalaryModalHistoryCell>
                  {parseIsoDateToLocalDateOrNull(taxItem.startDate)?.toString() ??
                    "-"}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell $isRightAligned>
                  {taxItem.value == null ? "-" : String(taxItem.value)}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell $isCentered>
                  {canDeleteHistoryItemForEmploymentStartDate(
                    taxItem.startDate,
                    props.employmentStartDate,
                  ) &&
                    canDeleteHistoryItemWithinLastMonthOrFuture(
                      taxItem.startDate,
                  ) && (
                    <SalaryModalDeleteButton
                      type="button"
                      aria-label={t("General.Remove")}
                      disabled={props.isSaving}
                      onClick={() => props.onDeleteTax(taxItem.id)}
                    >
                      <CustomIcon name="v2/trash" size="16px" />
                    </SalaryModalDeleteButton>
                  )}
                </SalaryModalHistoryCell>
              </SalaryModalHistoryRow>
            ))
          )}
        </SalaryModalHistoryTable>
      </SalaryModalHistorySection>

      {props.saveError.length > 0 && (
        <SalaryModalError>{props.saveError}</SalaryModalError>
      )}
    </Modal>
  );
};
